import {queryOptions} from '@github-ui/react-query'
import {reactFetchJSON} from '@github-ui/verified-fetch'

import type {ProjectSuggestionsResponse} from '../components/projects/types'

export type ProjectSuggestionsScope = 'recent' | 'search'
export type ProjectSuggestionsFilter = 'templates'

export const PROJECT_SUGGESTIONS_QUERY_KEY = 'repo-project-suggestions'

/**
 * Partial queryKey shared by every `projectSuggestionsQueryOptions` entry for a
 * single owner/repo, regardless of scope/query/filter. Mutation hooks (e.g.
 * `useUpsertProjectLinks`) use it for `invalidateQueries` partial matches, deriving
 * the key from this helper rather than hand-building it so it can't drift from the
 * factory.
 */
export function projectSuggestionsQueryKey(owner: string, repo: string) {
  return [PROJECT_SUGGESTIONS_QUERY_KEY, owner, repo] as const
}

export interface ProjectSuggestionsRequest {
  owner: string
  repo: string
  scope: ProjectSuggestionsScope
  query?: string
  filter?: ProjectSuggestionsFilter
}

export function buildProjectSuggestionsUrl({owner, repo, scope, query, filter}: ProjectSuggestionsRequest) {
  const params = new URLSearchParams({scope})
  const trimmedQuery = query?.trim() ?? ''
  if (scope === 'search' && trimmedQuery.length > 0) {
    params.set('q', trimmedQuery)
  }
  if (filter) {
    params.set('filter', filter)
  }
  // Encode the path segments to defend against unexpected `/`, `?`, or `..`
  // creeping in through `owner` / `repo` from a future caller. Real GitHub
  // logins/repo names use a safe charset, so encoding is a no-op for them.
  return `/${encodeURIComponent(owner)}/${encodeURIComponent(repo)}/projects/beta/suggestions/projects?${params.toString()}`
}

/**
 * Query options for the "Link a project" suggestions for a repository, backed by
 * `Repos::MemexesController#projects_suggestions`.
 *
 * Mirrors the autocomplete-picker convention used across the monorepo. The calling
 * component is responsible for:
 *   - debouncing free-text input (one request fires per `query` change),
 *   - gating fetching via `enabled` (e.g. `isDialogOpen` / non-empty search),
 *   - choosing whether to keep previous results during refetches via
 *     `placeholderData: keepPreviousData`.
 */
export function projectSuggestionsQueryOptions({owner, repo, scope, query, filter}: ProjectSuggestionsRequest) {
  const trimmedQuery = query?.trim() ?? ''
  // `query` only affects the URL when scope === 'search'. Excluding it from the
  // cache key for `recent` keeps cache hits stable across keystrokes when a caller
  // leaves search text in place while toggling back to the recent tab.
  const queryKeyQuery = scope === 'search' ? trimmedQuery : ''

  return queryOptions({
    queryKey: [...projectSuggestionsQueryKey(owner, repo), scope, queryKeyQuery, filter ?? null] as const,
    queryFn: async ({signal}) => {
      const url = buildProjectSuggestionsUrl({owner, repo, scope, query: queryKeyQuery, filter})
      // Forward the AbortSignal so superseded requests (e.g. user typed another
      // character before this fetch resolved) are cancelled rather than left to
      // run to completion against the backend.
      const response = await reactFetchJSON(url, {signal})
      if (!response.ok) {
        throw new Error(`Failed to fetch project suggestions: ${response.status}`)
      }
      return (await response.json()) as ProjectSuggestionsResponse
    },
  })
}
